import type { HtmlTagDescriptor } from 'vite'

/**
 * DCS motion tokens — the customer-site copy of the shared motion vocabulary.
 *
 * Injected as a constant `<style>` into every DCS site's `<head>` by
 * `dcsContentPlugin` (see its `transformIndexHtml`). This is the fleet-wide
 * seam: every site (vue-spa and VitePress) already wires `dcsContentPlugin`,
 * so a cms version bump ships these tokens to the whole fleet with zero
 * per-site source edits.
 *
 * Source of truth is the platform copy at
 * `packages/dcs-ui/src/styles/motion.css` and the `dcs-product-motion` skill —
 * keep the values here in lockstep. Values ONLY: reduced-motion stays a
 * per-component responsibility so no blanket `!important` rule leaks into a
 * site's isolated `.<slug>-page` design subtree.
 */
export const DCS_MOTION_TOKENS_CSS = `:root {
  --motion-duration-stagger: 40ms;
  --motion-duration-micro: 80ms;
  --motion-duration-quick: 150ms;
  --motion-duration-fast: 250ms;
  --motion-duration-medium: 350ms;
  --motion-duration-slow: 400ms;
  --motion-duration-very-slow: 500ms;
  --motion-ease-out: cubic-bezier(0.22, 1, 0.36, 1);
  --motion-ease-in-out: cubic-bezier(0.65, 0, 0.35, 1);
  --motion-ease-linear: linear;
  --motion-ease-bounce: cubic-bezier(0.34, 1.36, 0.64, 1);
  --motion-ease-bounce-strong: cubic-bezier(0.34, 3.85, 0.64, 1);
  --motion-distance-xs: 4px;
  --motion-distance-sm: 8px;
  --motion-distance-md: 12px;
  --motion-distance-lg: 24px;
  --motion-scale-lg: 0.96;
  --motion-scale-md: 0.97;
  --motion-scale-sm: 0.98;
  --motion-scale-xs: 0.99;
  --motion-blur-sm: 2px;
  --motion-blur-md: 3px;
  --motion-blur-lg: 8px;
}`

/**
 * The `<head>` tag descriptor for the motion-token block.
 *
 * `injectTo: 'head'`, NOT `'head-prepend'` — C-414. Vite's `head-prepend`
 * inserts immediately after the `<head>` open tag, i.e. AHEAD of the site's own
 * `<meta charset>`. This block is 858 bytes, so it pushed the encoding
 * declaration to byte 910 of the HTML spec's 1024-byte encoding-sniffing window
 * on every site in the fleet (measured on Iron Oak and Bryan's 2026-07-27, and
 * re-measured identical here), leaving ~90 bytes of headroom before every DCS
 * site silently mojibakes.
 *
 * `'head'` appends to the END of the `<head>` the site authored — which is
 * still ABOVE Vite's own build-time `<link rel=stylesheet>` / entry `<script>`,
 * because those are injected AFTER this `order: 'pre'` hook's tags. So the
 * per-site `:root` override still wins the cascade (that was the original
 * reason for `head-prepend`) AND the charset lands at byte ~46 with ~954 bytes
 * of headroom. Measured, not assumed — see `verify-honesty-rails.mjs` P12.
 *
 * If you ever move this back to `head-prepend`, the P12 rail in
 * `scripts/verify-honesty-rails.mjs` will fail: it asserts the charset comes
 * FIRST straight out of `dcsContentPlugin`, with no `dcsSeoPlugin` hoist.
 */
export function dcsMotionTokensStyleTag(): HtmlTagDescriptor {
  return {
    tag: 'style',
    attrs: { 'data-dcs-motion-tokens': '' },
    children: DCS_MOTION_TOKENS_CSS,
    injectTo: 'head',
  }
}
